/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useState, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { PageTitle } from '../../../_metronic/layout/core'
import MapView from './components/MapView'
import VesselsView from './components/VesselsView'
import { Route, Routes } from 'react-router-dom'
import { Vessel } from './core/_models'
import { useAuth } from '../../modules/auth'
import { getVesselDetails } from './core/_requests'
import { coordinateTextToDecimal } from '../../shared/utils/coordinateParsing'
import { useHomeDashboardData } from './hooks/useHomeDashboardData'
import FleetHealthBar from './components/FleetHealthBar'
import RiskPanel from './components/RiskPanel'
import ActionQueue from './components/ActionQueue'
import SchedulerTimeline from './components/SchedulerTimeline'
import InsightsPanel from './components/InsightsPanel'
import PortCalls from './components/PortCalls'
import FleetHealthDonut from './components/FleetHealthDonut'
import CriticalEvents from './components/CriticalEvents'
import { Loader2, AlertTriangle } from 'lucide-react'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { KpiCardSkeleton, CardSkeleton } from '../../shared/components/LoadingSkeletons'
import type { DashboardKpi, RiskItem } from './core/dashboard-types'
import {
  KpiDetailModal,
  RiskDetailModal,
  WorkQueueModal,
  PortCallsModal,
  CriticalEventsModal,
  InsightsModal,
  InsightDetailModal,
} from './components/DashboardModals'

/* ─────────────────────────────────────────────────────────────────────────
   DashboardPage — Fleet Command Center
   Layout: Map (top) → Fleet Health Bar → Analytics Row → Lower Grid → Footer
   ──────────────────────────────────────────────────────────────────────── */

type ModalType = 'kpi' | 'riskDetail' | 'workQueue' | 'portCalls' | 'criticalEvents' | 'insights' | 'insightDetail'

const DashboardPage: FC = () => {
  const { currentUser } = useAuth()
  const roleId = currentUser?.role?.id
  const vesselId = currentUser?.vessel?.id
  const vesselType = currentUser?.vessel?.vesselType
  const vesselName = currentUser?.vessel?.fleet_name
  const imoNumber = currentUser?.vessel?.imoNumber

  const dashboard = useHomeDashboardData()

  const [filteredVessels, setFilteredVessels] = useState<Vessel[]>([])
  const [vesselCount, setVesselCount] = useState<number>(0)

  /* ── Modal state ── */
  const [activeModal, setActiveModal] = useState<ModalType | null>(null)
  const [modalTitle, setModalTitle] = useState<string>('')
  const [selectedRisk, setSelectedRisk] = useState<RiskItem | null>(null)
  const [selectedInsightType, setSelectedInsightType] = useState<string>('')


  const openModal = useCallback((type: ModalType, title: string = '') => {
    setModalTitle(title)
    setActiveModal(type)
  }, [])

  const closeModal = useCallback(() => {
    setActiveModal(null)
    setModalTitle('')
    setSelectedRisk(null)
    setSelectedInsightType("")
  }, [])

  useEffect(() => {
    const fetchVessels = async () => {
      try {
        if (!vesselName || !imoNumber || !vesselType || !vesselId) {
          return
        }

        let actualCoords: [number, number] | undefined = undefined;
        let details: any = undefined;
        try {
          details = await getVesselDetails(Number(vesselId));
          if (details && details.latitude && details.longitude) {
            const lat = coordinateTextToDecimal(details.latitude, 'lat');
            const lon = coordinateTextToDecimal(details.longitude, 'lon');
            if (lat != null && lon != null) {
              actualCoords = [lon, lat];
            }
          }
        } catch (err) {
          console.error("Failed to fetch real vessel coordinates", err);
        }

        const vesselsWithData: Vessel = {
          id: Number(vesselId),
          fleet_name: vesselName,
          imoNumber,
          vesselType,
          status: details?.status || 'Unknown',
          port: details?.nextPort || '—',
          eta: details?.eta || '—',
          latitude: details?.latitude,
          longitude: details?.longitude,
          coords: actualCoords,
        }

        setFilteredVessels([vesselsWithData])
      } catch (error) {
        console.error('Failed to fetch vessel list:', error)
      }
    }

    if (roleId === 4) {
      fetchVessels()
    }
  }, [roleId, vesselName, imoNumber, vesselType, vesselId])

  /* ── KPI click handler — opens modal instead of navigate ── */
  const handleKpiClick = useCallback((kpi: DashboardKpi) => {
    const label = kpi.label
    if (label.toLowerCase().includes('risk') || label.toLowerCase().includes('fleet health')) {
      setModalTitle(`${label} — Detailed View`)
      setActiveModal('kpi')
    }
  }, [])



  return (
    <div className='space-y-4 md:space-y-5'>
      <ToastContainer
        position='top-center'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ top: '5rem', zIndex: 99999 }}
      />

      {/* ── MAP SECTION ── */}
      <div className='relative'>
        <div className='flex items-center justify-between mb-3 gap-2'>
          <div className='flex items-center gap-1' />
        </div>

        <div
          className='relative rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white'
          style={{ height: 'calc(100vh - 120px)', minHeight: '520px' }}
        >
          <div className='flex h-full w-full'>
            {roleId !== 4 && (
              <div className='hidden md:flex h-full flex-shrink-0 w-[304px] border-r border-slate-200 bg-white'>
                <VesselsView setFilteredVessels={setFilteredVessels} onVesselCountChange={setVesselCount} />
              </div>
            )}
            <div className='relative flex-1 min-w-0 h-full bg-[#dfeef2]'>
              <MapView vesselMarkers={filteredVessels} height='100%' vesselCount={vesselCount} />
            </div>
          </div>
        </div>
      </div>

      {/* ── ERROR BANNER ── */}
      {dashboard.error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-center justify-between text-red-800 text-sm">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span>{dashboard.error}</span>
          </div>
          {dashboard.refetch && (
            <button
              type="button"
              className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-900 rounded-lg font-medium transition-colors"
              onClick={() => dashboard.refetch?.()}
            >
              Retry
            </button>
          )}
        </div>
      )}

      {/* ── FLEET HEALTH KPI BAR ── */}
      {!dashboard.loaded ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCardSkeleton />
          <KpiCardSkeleton />
          <KpiCardSkeleton />
          <KpiCardSkeleton />
        </div>
      ) : (
        <FleetHealthBar kpis={dashboard.kpis} onKpiClick={handleKpiClick} />
      )}

      {/* ── ANALYTICS ROW ── */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6'>
        {/* Top Operational Risks — 5 cols */}
        <div className='md:col-span-1 lg:col-span-5 flex flex-col'>
          {!dashboard.loaded ? (
            <CardSkeleton lines={5} />
          ) : (
            <RiskPanel
              risks={dashboard.risks}
              onViewAll={() => openModal('kpi', 'All Operational Risks')}
              onRiskClick={(risk) => {
                setSelectedRisk(risk)
                setActiveModal('riskDetail')
              }}
            />
          )}
        </div>

        {/* Actionables / Work Queue — 4 cols */}
        <div className='md:col-span-1 lg:col-span-4 flex flex-col'>
          {!dashboard.loaded ? (
            <CardSkeleton lines={5} />
          ) : (
            <ActionQueue
              items={dashboard.workQueue}
              onViewAll={() => openModal('workQueue', 'Work Queue — All Items')}
            />
          )}
        </div>

        {/* Insights — 3 cols */}
        <div className='md:col-span-2 lg:col-span-3 flex flex-col'>
          {!dashboard.loaded ? (
            <CardSkeleton lines={3} />
          ) : (
            <InsightsPanel
              insights={dashboard.insights}
              onViewAll={() => openModal('insights', 'All Insights')}
              onInsightClick={(insight) => { setSelectedInsightType(String(insight.id)); setActiveModal('insightDetail'); setModalTitle(''); }}
            />
          )}
        </div>
      </div>

      {/* ── LOWER GRID ── */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 xl:grid-cols-12 gap-5 md:gap-6'>
        {/* Scheduler / Timeline — 4 cols */}
        <div className='lg:col-span-2 xl:col-span-4 flex flex-col'>
          {!dashboard.loaded ? (
            <CardSkeleton lines={4} />
          ) : (
            <SchedulerTimeline
              events={dashboard.timeline}
              onViewAll={() => openModal('workQueue', 'Schedule — All Planned Events')}
            />
          )}
        </div>

        {/* Upcoming Port Calls — 3 cols */}
        <div className='lg:col-span-2 xl:col-span-3 flex flex-col'>
          {!dashboard.loaded ? (
            <CardSkeleton lines={4} />
          ) : (
            <PortCalls
              portCalls={dashboard.portCalls}
              onViewAll={() => openModal('portCalls', 'All Upcoming Port Calls')}
            />
          )}
        </div>

        {/* Fleet Health Summary — 3 cols */}
        <div className='lg:col-span-1 xl:col-span-3 flex flex-col'>
          {!dashboard.loaded ? (
            <CardSkeleton lines={4} />
          ) : (
            <FleetHealthDonut
              health={dashboard.health}
              onViewAll={() => openModal('kpi', 'Fleet Health Summary')}
            />
          )}
        </div>

        {/* Upcoming Critical Events — 2 cols */}
        <div className='lg:col-span-1 xl:col-span-2 flex flex-col'>
          {!dashboard.loaded ? (
            <CardSkeleton lines={3} />
          ) : (
            <CriticalEvents
              events={dashboard.criticalEvents}
              onViewAll={() => openModal('criticalEvents', 'All Critical Events')}
            />
          )}
        </div>
      </div>

      {/* ── Footer note ── */}
      <div className='flex items-center justify-between text-[11px] text-slate-400 pt-2'>
        <span>
          All times are shown in vessel local time. Risks and insights are generated based on real-time data and configured
          thresholds.
        </span>
        <span>Data refreshed 2 min ago</span>
      </div>

      {/* ─────────────────────────────────────── */}
      {/*  MODALS — render inline, no redirect   */}
      {/* ─────────────────────────────────────── */}

      {/* KPI Detail / Risk List Modal */}
      {activeModal === 'kpi' && (
        <KpiDetailModal
          open
          onClose={closeModal}
          title={modalTitle}
          risks={dashboard.risks}
          workQueue={dashboard.workQueue}
        />
      )}

      {/* Risk Detail Modal */}
      {activeModal === 'riskDetail' && (
        <RiskDetailModal
          open
          onClose={closeModal}
          risk={selectedRisk}
        />
      )}

      {/* Work Queue Modal */}
      {activeModal === 'workQueue' && (
        <WorkQueueModal
          open
          onClose={closeModal}
          items={dashboard.workQueue}
        />
      )}

      {/* Port Calls Modal */}
      {activeModal === 'portCalls' && (
        <PortCallsModal
          open
          onClose={closeModal}
          portCalls={dashboard.portCalls}
        />
      )}

      {/* Critical Events Modal */}
      {activeModal === 'criticalEvents' && (
        <CriticalEventsModal
          open
          onClose={closeModal}
          events={dashboard.criticalEvents}
        />
      )}

      {/* Insights Modal */}
      {activeModal === 'insights' && (
        <InsightsModal
          open
          onClose={closeModal}
          insights={dashboard.insights}
        />
      )}

      {/* Insight Detail Modal */}
      {activeModal === 'insightDetail' && (
        <InsightDetailModal
          open
          onClose={closeModal}
          insightType={selectedInsightType}
          rawDefects={dashboard.rawDefects}
          rawOverdueJobs={dashboard.rawOverdueJobs}
          rawVoyages={dashboard.rawVoyages}
        />
      )}
    </div>
  )
}

const DashboardWrapper: FC = () => {
  const intl = useIntl()
  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({ id: 'MENU.DASHBOARD' })}</PageTitle>
      <Routes>
        <Route path='' element={<DashboardPage />} />
      </Routes>
    </>
  )
}

export { DashboardWrapper }
